import { GetElement } from "./utilities";

/**
 * Initializes Google Analytics by injecting the GTM script into the page.
 */
function InitialiseGoogleAnalytics(): void {
    const script = document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-T8R8HBWB');`;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-T8R8HBWB';
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    console.log("Google Analytics Initialised");
}

/**
 * Logs an informational message to the console.
 * @param message - The message to log
 */
function LogInfo(message: string): void {
    console.log(message);
}

/**
 * Sets the cookie acceptance preference.
 * @param accepted - true if cookies are accepted, false otherwise
 */
function SetCookiesAccepted(accepted: boolean): void {
    const cookieName = 'CookiesAccepted';
    const value = accepted ? 'true' : 'false';
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);

    const cookieString = encodeURIComponent(cookieName) + '=' + encodeURIComponent(value) +
        '; expires=' + expirationDate.toUTCString() +
        '; path=/' +
        '; Secure' +
        '; SameSite=Strict';

    document.cookie = cookieString;
}

/**
 * Retrieves the cookie acceptance preference.
 * @returns true if cookies are accepted, false if declined, null if no preference set
 */
function GetCookiesAccepted(): boolean | null {
    const cookieName = 'CookiesAccepted=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');

    for (let i = 0; i < cookieArray.length; i++) {
        const cookie = cookieArray[i].trim();
        if (cookie.indexOf(cookieName) === 0) {
            const value = cookie.substring(cookieName.length);
            return value === 'true';
        }
    }

    return null;
}

/**
 * Initializes the cookie consent dialog.
 * Shows the dialog if no preference has been set, or initializes analytics if already accepted.
 * @param dialogId - The ID of the cookie dialog element
 * @param isDevelopment - true if running in development mode
 */
export function InitialiseCookieDialog(dialogId: string, isDevelopment: boolean): void {
    const cookiesDecisionMade = GetCookiesAccepted();

    if (cookiesDecisionMade == null) {
        // Cookies have neither been accepted or declined.
        // Open the cookie modal.
        const dialog = GetElement(dialogId);
        if (dialog !== null && dialog instanceof HTMLDialogElement) {
            dialog.showModal();
        }
    } else if (cookiesDecisionMade === true) {
        if (isDevelopment === true) {
            LogInfo("Initialise Google Analytics");
        } else {
            InitialiseGoogleAnalytics();
        }
    }
}

/**
 * Sets the user's cookie decision and applies the appropriate actions.
 * @param dialogId - The ID of the cookie dialog element
 * @param accepted - true to accept cookies, false to decline, any other value logs an error
 * @param isDevelopment - true if running in development mode
 */
export function SetCookieDecision(dialogId: string, accepted: boolean, isDevelopment: boolean): void {
    const dialog = GetElement(dialogId);

    if (accepted === true) {
        SetCookiesAccepted(true);

        if (isDevelopment === true) {
            LogInfo("Initialise Google Analytics");
        } else {
            InitialiseGoogleAnalytics();
        }
    } else if (accepted === false) {
        SetCookiesAccepted(false);
    } else {
        console.error("SetCookieDecision accepted invalid");
    }

    if (dialog !== null && dialog instanceof HTMLDialogElement) {
        dialog.close();
    }
}
